/**
 * Cases by arms: how many trials of each passed every check. A cell opens the case.
 */
import { Flag } from 'lucide-react';
import type { EvalCaseSnapshot, EvalModel, EvalRunItem } from '../../types/evalTypes';
import { COST_NOTE, formatUsd, type CostSplit } from '../../utils/evalCost';
import { cellKey } from '../../utils/evalFormat';
import { ArmLabel } from '../ArmLabel';

interface CaseMatrixProps {
  run: EvalRunItem;
  /** For the arm labels. */
  models: EvalModel[];
  productionModel: string | undefined;
  /** For the tags. */
  cases: EvalCaseSnapshot[];
  /** `cellKey`s of the cells with at least one judge flag. */
  flaggedCells: Set<string>;
  /** Estimated cost of all trials of a case, all arms together. */
  caseCosts: Map<string, CostSplit>;
  selectedCaseId: string | null;
  selectedArmId: string | null;
  onSelect: (caseId: string, armId: string) => void;
}

const cellTone = (passed: number, trials: number): string => {
  if (trials === 0) return 'bg-gray-50 text-gray-400';
  if (passed === trials) return 'bg-green-100 text-green-900';
  if (passed === 0) return 'bg-red-100 text-red-900';
  return 'bg-amber-100 text-amber-900';
};

export const CaseMatrix = ({ run, models, productionModel, cases, flaggedCells, caseCosts, selectedCaseId, selectedArmId, onSelect }: CaseMatrixProps) => {
  const cell = (caseId: string, armId: string) =>
    run.summary.cells.find((candidate) => candidate.caseId === caseId && candidate.armId === armId) ?? { passed: 0, trials: 0 };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="font-medium px-4 py-2">Case</th>
            {run.arms.map((arm) => (
              <th key={arm.id} className="font-normal px-2 py-2 text-center">
                <ArmLabel arm={arm} models={models} productionModel={productionModel} compact />
              </th>
            ))}
            <th className="font-medium px-3 py-2 text-right" title={COST_NOTE}>
              Model cost
            </th>
            <th className="font-medium px-3 py-2 text-right" title={COST_NOTE}>
              Judge cost
            </th>
          </tr>
        </thead>
        <tbody>
          {run.caseIds.map((caseId) => {
            const info = cases.find((candidate) => candidate.id === caseId);
            return (
              <tr key={caseId} className={`border-t border-gray-100 ${caseId === selectedCaseId ? 'bg-blue-50' : ''}`}>
                <td className="px-4 py-1.5">
                  <span className="font-mono text-xs">{caseId}</span>
                  {info?.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${tag === 'risk' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {tag}
                    </span>
                  ))}
                </td>
                {run.arms.map((arm) => {
                  const { passed, trials } = cell(caseId, arm.id);
                  const selected = caseId === selectedCaseId && arm.id === selectedArmId;
                  return (
                    <td key={arm.id} className="px-2 py-1 text-center">
                      <button
                        type="button"
                        onClick={() => onSelect(caseId, arm.id)}
                        className={`w-full rounded px-2 py-1 text-xs font-medium inline-flex items-center justify-center gap-1 ${cellTone(passed, trials)} ${selected ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-gray-400'}`}
                      >
                        {trials === 0 ? '–' : `${passed}/${trials}`}
                        {flaggedCells.has(cellKey(caseId, arm.id)) && <Flag size={11} className="text-amber-700" aria-label="judge flagged" />}
                      </button>
                    </td>
                  );
                })}
                <td className="px-3 py-1 text-right text-xs text-gray-600">{formatUsd(caseCosts.get(caseId)?.model ?? null)}</td>
                <td className="px-3 py-1 text-right text-xs text-gray-400">{formatUsd(caseCosts.get(caseId)?.judge ?? null)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
